import { z } from 'zod';
import { LocalizedStringSchema } from './i18n';

/** Declarative property field schema (§6.4). The core renders and localizes these. */
const base = {
  /** Dot path inside `props`, e.g. `series.color`. */
  key: z.string().regex(/^[A-Za-z_$][\w$]*(\.[A-Za-z_$][\w$]*)*$/).max(200),
  label: LocalizedStringSchema,
  hint: LocalizedStringSchema.optional(),
  /** API 2.0: also shown in the platform's top bar (at most 4 per object type; not for `panel` fields). */
  quick: z.boolean().optional(),
};

export const FieldSchema = z.discriminatedUnion('type', [
  z.object({
    ...base,
    type: z.literal('number'),
    min: z.number().optional(),
    max: z.number().optional(),
    step: z.number().positive().optional(),
    default: z.number().optional(),
  }),
  z.object({ ...base, type: z.literal('color'), default: z.string().optional() }),
  z.object({
    ...base,
    type: z.literal('select'),
    options: z.array(z.object({ value: z.union([z.string(), z.number()]), label: LocalizedStringSchema })).min(1).max(200),
    default: z.union([z.string(), z.number()]).optional(),
  }),
  z.object({ ...base, type: z.literal('toggle'), default: z.boolean().optional() }),
  /** API 1.6: a date, stored as YYYY-MM-DD ('' when empty). */
  z.object({ ...base, type: z.literal('date'), default: z.string().regex(/^(\d{4}-\d{2}-\d{2})?$/).optional() }),
  z.object({
    ...base,
    type: z.literal('text'),
    multiline: z.boolean().optional(),
    maxLength: z.number().int().positive().optional(),
    default: z.string().optional(),
  }),
  /** Advanced section rendered by the module in a sandboxed iframe. */
  z.object({ ...base, type: z.literal('panel'), height: z.number().int().min(40).max(1200).optional() }),
]);
export type Field = z.infer<typeof FieldSchema>;
